
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Calendar, Star, Edit, Eye, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface Review {
  id: string;
  address_detail: string;
  rental_status: string;
  rating: number;
  created_at: string;
  rent: string;
  deposit_type: string;
  deposit_value: string;
  bedrooms: number;
  living_rooms: number;
  bathrooms: number;
  pet_allowed: boolean;
  comment: string;
}

interface MemberReviewsTabProps {
  userReviews: Review[];
  isLoading: boolean;
  onRefresh: () => void;
}

const MemberReviewsTab = ({ userReviews, isLoading, onRefresh }: MemberReviewsTabProps) => {
  const { toast } = useToast();

  const handleDelete = async (reviewId: string) => {
    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId);

      if (error) throw error;

      toast({
        title: '刪除成功',
        description: '投稿紀錄已成功刪除',
      });

      onRefresh();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast({
        variant: 'destructive',
        title: '刪除失敗',
        description: '無法刪除投稿紀錄，請稍後再試',
      });
    }
  };

  const getRatingDisplay = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`h-3 w-3 sm:h-4 sm:w-4 ${
          index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'viewed': { label: '已看房', variant: 'default' as const },
      'applied': { label: '已申請', variant: 'secondary' as const },
      'living': { label: '居住中', variant: 'destructive' as const }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { label: status, variant: 'outline' as const };
    return <Badge variant={statusInfo.variant} className="text-xs">{statusInfo.label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="text-lg sm:text-xl">我的投稿紀錄 ({userReviews.length})</CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-6">
        {userReviews.length > 0 ? (
          <div className="space-y-4 sm:space-y-6">
            {userReviews.map((review) => (
              <div key={review.id} className="border rounded-lg p-3 sm:p-4 lg:p-6 hover:shadow-md transition-shadow">
                <div className="space-y-3 sm:space-y-4">
                  {/* Header Section */}
                  <div className="flex flex-col space-y-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        <MapPin className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
                        <span className="font-medium text-sm sm:text-base break-words">
                          {review.address_detail || '地址未提供'}
                        </span>
                      </div>
                      {getStatusBadge(review.rental_status)}
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs sm:text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span>{new Date(review.created_at).toLocaleDateString('zh-TW')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {getRatingDisplay(review.rating)}
                        <span className="ml-1">({review.rating}/5)</span>
                      </div>
                    </div>
                  </div>

                  {/* Details Section */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
                    <div className="space-y-1">
                      <div className="break-words">
                        <span className="text-gray-500">租金:</span>
                        <span className="ml-1 font-medium">${review.rent}</span>
                      </div>
                      <div className="break-words">
                        <span className="text-gray-500">押金:</span>
                        <span className="ml-1 font-medium">{review.deposit_type} ${review.deposit_value}</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div>
                        <span className="text-gray-500">房型:</span>
                        <span className="ml-1">{review.bedrooms}房{review.living_rooms}廳{review.bathrooms}衛</span>
                      </div>
                      <div>
                        <span className="text-gray-500">寵物:</span>
                        <span className="ml-1">{review.pet_allowed ? '可養' : '不可養'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button asChild size="sm" variant="outline" className="flex-1 text-xs">
                      <Link to={`/reviews/${review.id}`}>
                        <Eye className="h-3 w-3 mr-1" />
                        查看
                      </Link>
                    </Button>
                    <Button asChild size="sm" variant="outline" className="flex-1 text-xs">
                      <Link to={`/reviews/${review.id}/edit`}>
                        <Edit className="h-3 w-3 mr-1" />
                        編輯
                      </Link>
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="flex-1 text-xs">
                          <Trash2 className="h-3 w-3 mr-1" />
                          刪除
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="mx-4 max-w-sm sm:max-w-lg">
                        <AlertDialogHeader>
                          <AlertDialogTitle>確認刪除</AlertDialogTitle>
                          <AlertDialogDescription>
                            您確定要刪除這篇投稿紀錄嗎？此操作無法復原。
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>取消</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(review.id)}>
                            確認刪除
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>

                  {/* Comment Section */}
                  {review.comment && (
                    <div className="mt-3 p-3 bg-gray-50 rounded">
                      <p className="text-xs sm:text-sm text-gray-700 break-words">{review.comment}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">您還沒有任何投稿紀錄</p>
            <Button asChild>
              <Link to="/submit">立即投稿</Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MemberReviewsTab;
